import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { css } from '@emotion/core'
import { Image, Paragraph, WhiteTheme } from '@fs/zion-ui'
import { DocumentPhoto, NoticeLoading } from '@fs/zion-icon'
import { usePortraitSelector as useImageSelector } from '@fs/zion-portrait-selector'
import { useTranslation } from 'react-i18next'

import ImagePickerOverlay from './ImagePickerOverlay/ImagePickerOverlay'
import Caption from './Caption/Caption'
import { getMemory } from '../../../../services/MemoriesAPI/memoriesAPI'
import { MissingMemoryIcon } from '../../../MemoryIconComponents/MemoryIconComponents'
import useImageHandlers from '../../../../hooks/useImageHandlers/useImageHandlers'
import useImageCropping from '../../../../hooks/useImageCropping/useImageCropping'

const imagePickerCSS = (noCaption) => css`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: ${noCaption ? '0' : '5px'};
  width: 100%;
  height: 100%;
  border: none !important;
`

const imageButtonContainerCSS = (noCaption) => css`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: ${noCaption ? '0' : '8px'};
`

const captionContainerCSS = (noCaption) => css`
  min-height: ${noCaption ? '0' : '32px'}; /* Reserve space for caption even when empty, unless noCaption is true */
  width: 100%;
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
`

const backgroundAndIconCss = (isFreeformPicker) => {
  const gradientColors = isFreeformPicker
    ? { darker: '#c6d64d', lighter: '#edefc9' }
    : { darker: '#fedd73', lighter: '#faecb4' }

  return css`
    background: linear-gradient(to top, ${gradientColors.darker}, ${gradientColors.lighter});
    display: flex;
    width: 100%;
    height: 100%;
    flex: 1;
    align-items: center;
    justify-content: center;
    border: none;
    color: black;

    &:focus {
      outline: 3px solid black;
      outline-offset: 2px;
    }

    @media print {
      p {
        display: none;
      }
    }
  `
}

const selectNewImageButtonCss = css`
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  display: block;
  border: none;
  background: transparent;
  padding: 0;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: transparent;
    transition: background-color 0.3s ease-in-out;
  }

  &:hover::after {
    background: rgba(0, 0, 0, 0.7);
  }

  &:hover > .hover-show {
    opacity: 1;
  }

  &:focus {
    outline: 3px solid black;
    outline-offset: 2px;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
  }
`

const imageHoverContent = css`
  opacity: 0;
  z-index: 1;
  position: absolute;
  top: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;

  transition: opacity 0.2s ease-in-out;
`

const imagePickerBorderCss = css`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;

  @media print {
    background-image: none;
    padding: 0;
  }
`

const overlayAddImageContentCss = css`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const ImageDisplay = ({
  savedImage,
  caption,
  isPrintView,
  missingMemory,
  title,
  croppedImageUrl,
  defaultImage,
  isTreeDesignsPage,
}) => {
  const [t] = useTranslation()

  const altText =
    title || caption || savedImage?.imageTitle || t('ImagePicker.defaultAltText', 'Image from your memories')

  // Use cropped image if available and valid, otherwise fall back to original image
  const isValidCroppedImage = croppedImageUrl?.startsWith('data:image/')
  const imageUrl = isValidCroppedImage ? croppedImageUrl : savedImage?.url

  if (isPrintView && !missingMemory) {
    return (
      <img
        src={imageUrl}
        alt={altText}
        style={{
          objectFit: defaultImage && isTreeDesignsPage ? 'contain' : 'cover',
          height: '100%',
          width: '100%',
        }}
      />
    )
  }

  return (
    <>
      {missingMemory ? (
        <MissingMemoryIcon
          label={t('ImagePicker.missingMemoryIcon.label', 'This image is no longer in your memories.')}
        />
      ) : (
        <>
          <div className="hover-show" css={imageHoverContent}>
            <WhiteTheme>
              <div css={overlayAddImageContentCss}>
                <DocumentPhoto size="md" />
                <Paragraph centered size="xs">
                  {t('ImagePicker.addImage.label', 'Add Image')}
                </Paragraph>
              </div>
            </WhiteTheme>
          </div>
          <Image
            src={imageUrl}
            style={{
              objectFit: defaultImage && isTreeDesignsPage ? 'contain' : 'cover',
              height: '100%',
              width: '100%',
            }}
            alt={altText}
          />
        </>
      )}
    </>
  )
}

const ImageButton = ({
  savedImage,
  imageOverlay,
  caption,
  isPrintView,
  missingMemory,
  title,
  croppedImageUrl,
  cropRegionRef,
  processingCrop,
  defaultImage,
  isTreeDesignsPage,
  isFreeformPicker,
}) => {
  const [t] = useTranslation()

  const getImagePickerAriaLabel = () => {
    if (missingMemory) {
      return t(
        'ImagePicker.missingImageMemory.label',
        'This image memory is missing, open the image selector to choose a new image.'
      )
    }
    if (savedImage?.url) {
      if (caption) {
        return t(
          'ImagePicker.openPickerWithCaption.label',
          'Image titled: {caption}, open the image selector to choose a new image.',
          {
            caption,
          }
        )
      }
      return t('ImagePicker.openPickerWithImage.label', 'Image present, open the image selector to choose a new image.')
    }
    return t('ImagePicker.openPicker.label', 'Open Image Selector')
  }
  if (isPrintView && !missingMemory) {
    return (
      <ImageDisplay
        savedImage={savedImage}
        caption={caption}
        isPrintView={isPrintView}
        missingMemory={missingMemory}
        title={title}
        croppedImageUrl={croppedImageUrl}
        defaultImage={defaultImage}
        isTreeDesignsPage={isTreeDesignsPage}
      />
    )
  }

  if (savedImage?.url || missingMemory) {
    return (
      <button
        type="button"
        css={selectNewImageButtonCss}
        onClick={imageOverlay.open}
        aria-label={getImagePickerAriaLabel()}
        disabled={processingCrop}
        ref={cropRegionRef}
      >
        <ImageDisplay
          savedImage={savedImage}
          caption={caption}
          isPrintView={isPrintView}
          missingMemory={missingMemory}
          title={title}
          croppedImageUrl={croppedImageUrl}
          defaultImage={defaultImage}
          isTreeDesignsPage={isTreeDesignsPage}
        />
      </button>
    )
  }

  return (
    <div css={imagePickerBorderCss} ref={cropRegionRef}>
      <button
        type="button"
        onClick={imageOverlay.open}
        css={backgroundAndIconCss(isFreeformPicker)}
        aria-label={t('ImagePicker.openPicker.label', 'Open Image Selector')}
        disabled={processingCrop}
      >
        {processingCrop ? (
          <NoticeLoading size="md" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <DocumentPhoto size="md" />
            <Paragraph size="xs">{t('ImagePicker.addImage.label', 'Add Image')}</Paragraph>
          </div>
        )}
      </button>
    </div>
  )
}

const ImagePicker = ({
  pageNumber,
  memoryId,
  imageSource = undefined, // used for custom images to be replaced
  isPrintView = false,
  caption = '',
  title = '',
  positionIndex = 0,
  noCaption = false,
  isHeading = false,
  isTreeDesignsPage = false,
  cropArea: currentCropArea,
  handleMissingMemoryStatus,
  customLayoutElementId,
  onUpdateCustomLayoutElementData,
  onContentReady,
  isFreeformPicker = false,
  isPlaceholder = false,
  onCancelPlaceholder,
}) => {
  const [savedImage, setSavedImage] = useState({ url: imageSource })
  const [loading, setLoading] = useState(false)
  const [missingMemory, setMissingMemory] = useState(false)
  const [selectedImage, setSelectedImage] = useState()

  const defaultHandleMissingMemoryStatus = useCallback(() => {}, [])
  const stableHandleMissingMemoryStatus = handleMissingMemoryStatus || defaultHandleMissingMemoryStatus

  // Track if overlay was auto-opened for placeholder elements
  const hasAutoOpenedRef = useRef(false)
  const prevIsOpenRef = useRef(false)

  const {
    cropRegionRef,
    containerDimensions,
    croppedImageUrl,
    processingCrop,
    isWaitingForCroppedImage,
    handleRemove: croppingHandleRemove,
    handleSave: croppingHandleSave,
  } = useImageCropping({
    initialCropArea: currentCropArea,
    savedImageUrl: savedImage?.url,
    memoryId,
    pageNumber,
    positionIndex,
  })

  const imageSelectorOptions = useMemo(
    () => ({
      alwaysResetImageRotation: true,
      imageSelectorMode: true,
      targetDimensions: containerDimensions,
    }),
    [containerDimensions]
  )

  const { cropArea, ...imageOverlay } = useImageSelector(imageSelectorOptions)

  const [isOverlayOpen, setIsOverlayOpen] = useState(imageOverlay.isOpen)

  useEffect(() => {
    let isMounted = true

    async function fetchMemories() {
      setLoading(true)
      try {
        if (!memoryId) {
          if (isMounted) {
            setSavedImage({ url: imageSource })
            setMissingMemory(false)
          }
          return
        }

        const response = await getMemory(memoryId)

        if (!isMounted) {
          return
        }

        if (response === null) {
          stableHandleMissingMemoryStatus({ missing: true })
          setMissingMemory(true)
          setSavedImage({ url: undefined })
          return
        }

        const { url, title: imageTitle } = response
        setSavedImage({ url, imageTitle })
        setMissingMemory(false)
      } catch (error) {
        if (!isMounted) {
          return
        }

        console.error('Failed to fetch memory image', error)
        stableHandleMissingMemoryStatus({ missing: true })
        setMissingMemory(true)
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchMemories()

    return () => {
      isMounted = false
    }
  }, [stableHandleMissingMemoryStatus, imageSource, memoryId])

  // Force re-render so we don't end up with a ghost dialog preventing the click of the button
  useEffect(() => {
    setIsOverlayOpen(imageOverlay.isOpen)
  }, [imageOverlay.isOpen])

  // Auto-open overlay for placeholder elements
  useEffect(() => {
    if (isPlaceholder && !hasAutoOpenedRef.current && !savedImage?.url && !loading) {
      hasAutoOpenedRef.current = true
      imageOverlay.open()
    }
  }, [isPlaceholder, savedImage?.url, loading, imageOverlay])

  // Detect cancellation (overlay closes without save)
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current
    const isNowClosed = !imageOverlay.isOpen

    if (wasOpen && isNowClosed && isPlaceholder && !savedImage?.url) {
      // User canceled without saving
      onCancelPlaceholder?.()
    }

    prevIsOpenRef.current = imageOverlay.isOpen
  }, [imageOverlay.isOpen, isPlaceholder, savedImage?.url, onCancelPlaceholder])

  // Notify parent when content is fully ready (loaded and cropped)
  useEffect(() => {
    const isContentReady = !loading && !processingCrop && !isWaitingForCroppedImage
    if (isContentReady && onContentReady) {
      onContentReady()
    }
  }, [loading, processingCrop, isWaitingForCroppedImage, onContentReady])

  const {
    onSave: originalOnSave,
    onRemove: originalOnRemove,
    updateImage: originalUpdateImage,
    onUpload,
  } = useImageHandlers({
    memoryId,
    setSavedImage,
    setSelectedImage,
    updatePageContentProps: {
      caption,
      positionIndex,
      pageNumber,
      title,
    },
    cropArea,
    customLayoutElementId,
    onUpdateCustomLayoutElementData,
  })

  const handleRemove = useCallback(() => {
    croppingHandleRemove(originalOnRemove)
  }, [croppingHandleRemove, originalOnRemove])

  const handleUpdateImage = useCallback(
    (artifact) => {
      originalUpdateImage(artifact)
    },
    [originalUpdateImage]
  )

  const onSave = useCallback(
    async (image) => {
      await croppingHandleSave({ image, cropArea, onSave: originalOnSave })
    },
    [croppingHandleSave, cropArea, originalOnSave]
  )

  // Handle print view cases first (independent of loading state)
  if (isPrintView && !savedImage?.url) {
    if (caption || title) {
      return (
        <div css={imagePickerCSS(noCaption)}>
          {!noCaption && (
            <Caption
              memoryId={memoryId}
              positionIndex={positionIndex}
              pageNumber={pageNumber}
              caption={caption}
              title={title}
              isPrintView={isPrintView}
              isHeading={isHeading}
              customLayoutElementId={customLayoutElementId}
              onUpdateCustomLayoutElementData={onUpdateCustomLayoutElementData}
            />
          )}
        </div>
      )
    }
    return null
  }

  if (loading || processingCrop || isWaitingForCroppedImage) {
    return (
      <div css={backgroundAndIconCss(isFreeformPicker)}>
        <NoticeLoading size="md" color="black" />
      </div>
    )
  }

  return (
    <>
      <div css={imagePickerCSS(noCaption)}>
        <div css={imageButtonContainerCSS(noCaption)}>
          <ImageButton
            savedImage={savedImage}
            imageOverlay={imageOverlay}
            caption={caption}
            isPrintView={isPrintView}
            missingMemory={missingMemory}
            title={title}
            croppedImageUrl={croppedImageUrl}
            cropRegionRef={cropRegionRef}
            processingCrop={processingCrop}
            defaultImage={imageSource}
            isTreeDesignsPage={isTreeDesignsPage}
            isFreeformPicker={isFreeformPicker}
          />
        </div>

        {!isTreeDesignsPage && (
          <div css={captionContainerCSS(noCaption)}>
            {!noCaption && (
              <Caption
                memoryId={memoryId}
                positionIndex={positionIndex}
                pageNumber={pageNumber}
                caption={caption}
                title={title}
                isPrintView={isPrintView}
                isHeading={isHeading}
                customLayoutElementId={customLayoutElementId}
                onUpdateCustomLayoutElementData={onUpdateCustomLayoutElementData}
              />
            )}
          </div>
        )}
      </div>
      {isOverlayOpen && (
        <ImagePickerOverlay
          overlay={imageOverlay}
          savedImage={savedImage}
          memoryId={memoryId}
          onSave={onSave}
          onRemove={handleRemove}
          updateImage={handleUpdateImage}
          onUpload={onUpload}
          selectedImage={selectedImage}
          targetDimensions={containerDimensions}
        />
      )}
    </>
  )
}

export default ImagePicker
